/**
 * LiveKit-Verbindungskarte im Reiter „API-Schlüssel".
 *
 * Vier Felder (URL, API Key, API Secret + optional SIP user/pass) +
 * ein „Verbindung testen"-Button. Der Probe-Call ruft das Cloud Function
 * `testLiveKitConnection` auf, das (1) gegen die LK HTTP-API checkt und
 * (2) bei Erfolg die SIP-Inbound-URI ableitet, die der User in seinen
 * Twilio Elastic SIP Trunk einträgt.
 *
 * Status-Badges folgen exakt dem Muster der LLM-API-Keys-Karte: ✓
 * gespeichert / fehlt, plus eine zusätzliche „verifiziert"-Anzeige nach
 * erfolgreichem Probe.
 */
import * as React from 'react';
import {
  CheckCircle2,
  Copy,
  Loader2,
  PhoneIncoming,
  ShieldCheck,
  Sparkles,
  XCircle,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../../../../components/ui/card';
import { Input } from '../../../../components/ui/input';
import { Label } from '../../../../components/ui/label';
import { Button } from '../../../../components/ui/button';
import { useToast } from '../../../../components/ui/toast';
import { useSecretStatus } from '../../../../hooks/useSecretStatus';
import { useTestLiveKitConnection } from '../../../../hooks/useLiveKit';
import { usePhoneSettingsForm } from '../PhoneSettingsContext';
import { cn } from '../../../../lib/utils';

export function LiveKitCard() {
  const { form, setForm } = usePhoneSettingsForm();
  const status = useSecretStatus();
  const probe = useTestLiveKitConnection();
  const { toast } = useToast();

  const lkUrl = form.livekitUrl ?? '';
  const sipUri = form.livekitSipUri ?? '';
  const secrets = form.secrets ?? {};
  const draftKey = secrets.livekitApiKey ?? '';
  const draftSecret = secrets.livekitApiSecret ?? '';
  const draftSipUser = secrets.livekitSipUsername ?? '';
  const draftSipPass = secrets.livekitSipPassword ?? '';

  const lkStatus = status.data?.livekit;
  const keyStored = !!lkStatus?.apiKey;
  const secretStored = !!lkStatus?.apiSecret;
  const sipUserStored = !!lkStatus?.sipUsername;
  const sipPassStored = !!lkStatus?.sipPassword;
  const fullyConfigured = !!lkUrl && keyStored && secretStored;

  function patchSecret(key: keyof NonNullable<typeof form.secrets>, value: string) {
    setForm((p) => ({
      ...p,
      secrets: { ...(p.secrets ?? {}), [key]: value },
    }));
  }

  async function onProbe() {
    try {
      // Probe with the form-state values so the user can validate
      // BEFORE persisting. Empty form fields fall back to stored creds
      // server-side.
      const r = await probe.mutateAsync({
        url: lkUrl || undefined,
        apiKey: draftKey || undefined,
        apiSecret: draftSecret || undefined,
      });
      setForm((p) => ({ ...p, livekitSipUri: r.sipUri }));
      toast({
        title: 'LiveKit erreichbar',
        description: `SIP-Inbound-URI: ${r.sipUri}`,
      });
    } catch (err) {
      toast({
        title: 'LiveKit-Test fehlgeschlagen',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  async function copyToClipboard(value: string, label: string) {
    try {
      await navigator.clipboard.writeText(value);
      toast({ title: `${label} kopiert` });
    } catch {
      toast({ title: 'Kopieren fehlgeschlagen', variant: 'destructive' });
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Sparkles className="h-5 w-5 text-primary" />
          LiveKit (HD-Audio Add-on)
        </CardTitle>
        <CardDescription>
          Optionales Add-on für hochwertiges WebRTC-Audio (Opus 48 kHz)
          statt μ-law 8 kHz. Wird sowohl vom Browser-Test-Chat als auch
          — wenn unter „Anbieter & Nummer" auf LiveKit umgestellt — von
          eingehenden Telefon-Anrufen via SIP-Trunk genutzt. Credentials
          aus dem LiveKit-Cloud-Projekt → Settings → Keys.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Status banner */}
        <div
          className={cn(
            'flex items-center gap-2 rounded-md border px-3 py-2 text-xs',
            fullyConfigured
              ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200'
              : 'border-border bg-muted/30 text-muted-foreground',
          )}
        >
          {fullyConfigured ? (
            <CheckCircle2 className="size-4 text-emerald-400" />
          ) : (
            <XCircle className="size-4 text-muted-foreground" />
          )}
          {fullyConfigured
            ? 'LiveKit-Verbindung konfiguriert. Du kannst sie im Tab „Anbieter & Nummer" als Pipeline-Transport aktivieren.'
            : 'Noch nicht vollständig konfiguriert — URL, API-Key und API-Secret werden benötigt.'}
        </div>

        {/* URL */}
        <div className="space-y-1.5">
          <Label htmlFor="livekit-url">Projekt-URL</Label>
          <Input
            id="livekit-url"
            placeholder="wss://my-project-xxxxxxxx.livekit.cloud"
            value={lkUrl}
            onChange={(e) => setForm((p) => ({ ...p, livekitUrl: e.target.value }))}
            autoComplete="off"
          />
          <p className="text-xs text-muted-foreground">
            Findest du im LK-Dashboard im Projekt-Dropdown (klicken → URL erscheint).
          </p>
        </div>

        {/* API Key + Secret */}
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <Label htmlFor="livekit-api-key">API Key</Label>
              <StatusPill present={keyStored} />
            </div>
            <Input
              id="livekit-api-key"
              type="password"
              placeholder={keyStored ? '••••••••  (leer = unverändert)' : 'API… aus LK Settings → Keys'}
              value={draftKey}
              onChange={(e) => patchSecret('livekitApiKey', e.target.value)}
              autoComplete="off"
            />
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <Label htmlFor="livekit-api-secret">API Secret</Label>
              <StatusPill present={secretStored} />
            </div>
            <Input
              id="livekit-api-secret"
              type="password"
              placeholder={
                secretStored ? '••••••••  (leer = unverändert)' : 'Secret nur einmal sichtbar'
              }
              value={draftSecret}
              onChange={(e) => patchSecret('livekitApiSecret', e.target.value)}
              autoComplete="off"
            />
          </div>
        </div>

        {/* SIP credentials (optional) */}
        <details className="rounded-md border border-border p-3">
          <summary className="cursor-pointer text-sm font-medium">
            <PhoneIncoming className="mr-1.5 inline size-3.5 -translate-y-0.5" />
            SIP-Inbound-Credentials (optional — nur falls du am LK-Trunk
            Username+Password-Auth aktiviert hast)
          </summary>
          <div className="mt-3 grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Label htmlFor="livekit-sip-user">SIP Username</Label>
                <StatusPill present={sipUserStored} />
              </div>
              <Input
                id="livekit-sip-user"
                type="text"
                placeholder={sipUserStored ? 'gespeichert' : 'aus LK-Trunk-Auth'}
                value={draftSipUser}
                onChange={(e) => patchSecret('livekitSipUsername', e.target.value)}
                autoComplete="off"
              />
            </div>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Label htmlFor="livekit-sip-pass">SIP Password</Label>
                <StatusPill present={sipPassStored} />
              </div>
              <Input
                id="livekit-sip-pass"
                type="password"
                placeholder={sipPassStored ? '••••••••' : 'aus LK-Trunk-Auth'}
                value={draftSipPass}
                onChange={(e) => patchSecret('livekitSipPassword', e.target.value)}
                autoComplete="off"
              />
            </div>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Diese Werte hinterlegst du auch in Twilio (Elastic SIP Trunk →
            Authentication) — Twilio nutzt sie, um sich beim LK-Trunk zu
            authentifizieren. Wenn du stattdessen IP-Allowlisting an deinem
            LK-Trunk konfiguriert hast, kannst du diese Felder leer lassen.
          </p>
        </details>

        {/* SIP URI surface (post-probe) */}
        {sipUri && (
          <div className="space-y-1.5 rounded-md border border-sky-500/30 bg-sky-500/5 p-3">
            <div className="flex items-center gap-2 text-xs font-medium text-sky-200">
              <ShieldCheck className="size-3.5" />
              SIP-Inbound-URI (in Twilio Elastic SIP Trunk → Origination URI eintragen)
            </div>
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate rounded bg-background px-2 py-1 font-mono text-xs">
                {sipUri}
              </code>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 px-2"
                onClick={() => copyToClipboard(sipUri, 'SIP-URI')}
              >
                <Copy className="size-3.5" />
              </Button>
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onProbe}
            disabled={probe.isPending || (!draftKey && !keyStored) || (!draftSecret && !secretStored) || !lkUrl}
          >
            {probe.isPending ? (
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
            ) : (
              <ShieldCheck className="mr-2 h-3.5 w-3.5" />
            )}
            Verbindung testen
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground">
          Hinweis: Speichern erfolgt über den globalen „Speichern"-Button
          oben — Test funktioniert auch BEVOR du speicherst (probt direkt
          gegen die im Formular eingetragenen Werte).
        </p>
      </CardContent>
    </Card>
  );
}

function StatusPill({ present }: { present: boolean }) {
  return present ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] text-emerald-400">
      <CheckCircle2 className="h-3 w-3" /> gespeichert
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2 py-0.5 text-[10px] text-amber-400">
      <XCircle className="h-3 w-3" /> fehlt
    </span>
  );
}
